// WorkingHours.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import CalendarPicker from './CalendarPicker';

const API_BASE = "https://bunny-klawz.uc.r.appspot.com";

const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
];

const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday
    const newDate = new Date(d);
    newDate.setDate(d.getDate() - day); // move back to Sunday
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

const formatWeekRange = (startDate) => {
    const start = new Date(startDate);
    const end = new Date(startDate);
    end.setDate(start.getDate() + 6);

    const options = { month: "short", day: "numeric" };
    const year = end.getFullYear();

    return `${start.toLocaleDateString("en-US", options)} - ${end.toLocaleDateString("en-US", options)}, ${year}`;
};

const formatMonthDate = (date) => {
    const monthDate = { month: "short", day: "numeric" }

    return `${new Date(formatToLocalDateKey(date)).toLocaleDateString("en-US", monthDate)}`

}

// Helper: Format to YYYY-MM-DD in local time (no UTC shift)
const formatToLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};


const WorkingHours = () => {
    const [workingHours, setWorkingHours] = useState([]);
    const [filteredHours, setFilteredHours] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [editingDay, setEditingDay] = useState(null);

    const [newHour, setNewHour] = useState({
        start: "",
        end: "",
        nailtech: "Leah",
    });

    useEffect(() => {
        fetchWorkingHours();
    }, []);

    const fetchWorkingHours = async () => {
        try {
            const res = await axios.get(`${API_BASE}/working-hours`);
            const data = res.data;

            setWorkingHours(data);
            const weeksList = generateWeeks(data);
            setWeeks(weeksList);

            // ✅ Only set selectedWeek if it hasn't been set yet
            if (!selectedWeek) {
                const thisWeekStart = getStartOfWeek(new Date());
                const localKey = formatToLocalDateKey(thisWeekStart);
                setSelectedWeek(localKey);
            }

        } catch (err) {
            console.error("❌ Error fetching working hours:", err);
        }
    };

    const generateWeeks = (data) => {
        const weekMap = new Map();

        // Get reference weeks in local time
        const now = new Date();

        const thisWeek = getStartOfWeek(now);
        const lastWeek = getStartOfWeek(new Date(thisWeek.getTime() - 7 * 24 * 60 * 60 * 1000));
        const nextWeek = getStartOfWeek(new Date(thisWeek.getTime() + 7 * 24 * 60 * 60 * 1000));

        // Add default weeks
        [lastWeek, thisWeek, nextWeek].forEach((weekStart) => {
            const key = formatToLocalDateKey(weekStart);
            weekMap.set(key, weekStart);
        });

        // Add weeks from data
        data.forEach((item) => {
            const dateStr = item.start;
            if (!dateStr) return;

            const startOfWeek = getStartOfWeek(new Date(dateStr));
            if (startOfWeek < lastWeek) return;

            const key = formatToLocalDateKey(startOfWeek);
            if (!weekMap.has(key)) {
                weekMap.set(key, startOfWeek);
            }
        });

        // Return sorted weeks
        return Array.from(weekMap.values()).sort((a, b) => a - b);
    };

    useEffect(() => {
        if (selectedWeek) {

            const start = new Date(selectedWeek);
            start.setDate(start.getDate() + 1); // shift start to Sunday
            start.setHours(0, 0, 0, 0);

            const end = new Date(start);
            end.setDate(start.getDate() + 6); // end on Saturday
            end.setHours(23, 59, 59, 999);

            const filtered = workingHours.filter(hour => {
                const date = new Date(hour.start);
                return date >= start && date <= end;
            });

            setFilteredHours(filtered);
        }
    }, [selectedWeek, workingHours]);

    const groupedByDay = daysOfWeek.reduce((acc, day) => {
        acc[day] = [];
        return acc;
    }, {});

    const weekStart = new Date(selectedWeek);
    weekStart.setHours(0, 0, 0, 0);

    filteredHours.forEach((hour) => {
        const localDate = new Date(hour.start);
        localDate.setSeconds(0, 0);

        for (let i = 0; i <= 7; i++) {
            const compareDate = new Date(weekStart);
            compareDate.setDate(compareDate.getDate() + i);
            compareDate.setHours(23, 59, 59, 999);

            if (localDate.toDateString() === compareDate.toDateString()) {
                const dayName = daysOfWeek[compareDate.getDay()];
                groupedByDay[dayName].push(hour);
                break;
            }
        }
    });


    const handleDateChange = (e) => {
        console.log('Selected date:', e.target.value);
    };

    const handleCalendarDateSelect = (dateStr) => {
        const date = new Date(dateStr);
        const startOfWeek = getStartOfWeek(date);
        const localKey = formatToLocalDateKey(startOfWeek);

        // Always regenerate weeks from working hours
        const baseWeeks = generateWeeks(workingHours);

        const isAlreadyInList = baseWeeks.some(
            (weekDate) => formatToLocalDateKey(weekDate) === localKey
        );

        let newWeeks = [...baseWeeks];
        if (!isAlreadyInList) {
            newWeeks.push(startOfWeek);
            newWeeks.sort((a, b) => a - b);
        }

        setWeeks(newWeeks);
        setSelectedWeek(localKey);
    };
    const formatLocalISO = (date) => {
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };


    const handleAddWorkingHour = async (day, dayIndex) => {
        if (!newHour.start || !newHour.end || !newHour.nailtech) {
            alert("Please fill in all fields.");
            return;
        }

        try {
            // const dayIndex = daysOfWeek.indexOf(day);
            const [startHour, startMin] = newHour.start.split(":");
            const [endHour, endMin] = newHour.end.split(":");

            const [year, month, day] = selectedWeek.split("-").map(Number);

            // Safe construction of weekStart with no UTC issues
            const baseDate = new Date(year, month - 1, day);

            // Safely calculate the exact day of the week
            const localDate = new Date(
                baseDate.getFullYear(),
                baseDate.getMonth(),
                baseDate.getDate() + dayIndex
            );

            const startDateTime = new Date(
                localDate.getFullYear(),
                localDate.getMonth(),
                localDate.getDate(),
                Number(startHour),
                Number(startMin)
            );

            const endDateTime = new Date(
                localDate.getFullYear(),
                localDate.getMonth(),
                localDate.getDate(),
                Number(endHour),
                Number(endMin)
            );

            await axios.post(`${API_BASE}/working-hours`, {
                start: startDateTime.toISOString(),
                end: endDateTime.toISOString(),
                nailtech: newHour.nailtech,
            });

            setNewHour({ start: "", end: "", nailtech: "Leah" });
            // setEditingDay(null);
            fetchWorkingHours();
        } catch (err) {
            console.error("❌ Error adding working hour:", err);
            alert("Failed to add working hour.");
        }
    };

    const handleDeleteWorkingHour = async (id) => {
        try {
            await axios.delete(`${API_BASE}/working-hours/${id}`);
            fetchWorkingHours(); // Refresh the list after deletion
            alert("Please note that some individuals might be checking out in the next 5-7 min. This may result in a new pending appointment.");
        } catch (err) {
            if (err.response && err.response.status === 400) {
                alert("This working hour cannot be deleted because it overlaps with an existing appointment.");
            } else {
                console.error("❌ Error deleting working hour:", err);
                alert("Failed to delete working hour.");
            }
        }
    };

    return (
        <>
            <div className="WorkingHours">
                <div className="WorkingHoursContent">
                    {/* <h2>Working Hours</h2> */}

                    {/* Week Selector */}
                    {/* <div className="WeekSelector">
                        <select
                            value={selectedWeek || ""}
                            onChange={(e) => setSelectedWeek(e.target.value)}
                        >
                            {weeks.map((weekStart) => (
                                <option key={weekStart.toISOString()} value={weekStart.toISOString().slice(0, 10)}>
                                    {formatWeekRange(weekStart)}
                                </option>
                            ))}
                        </select>
                        <CalendarPicker />
                    </div> */}

                    <div className="WeekSelector">
                        <select
                            value={selectedWeek || ""}
                            onChange={(e) => setSelectedWeek(e.target.value)}
                        >
                            {weeks.map((weekStart, index) => {
                                const dateKey = formatToLocalDateKey(weekStart);
                                let label;

                                // if (index === 0) label = "Last Week";
                                // else if (index === 1) label = "This Week";
                                // else if (index === 2) label = "Next Week";
                                // else label = formatWeekRange(weekStart);

                                label = formatWeekRange(weekStart);

                                return (
                                    <option key={dateKey} value={dateKey}>
                                        {label}
                                    </option>
                                );
                            })}
                        </select>

                        <CalendarPicker onDateSelect={handleCalendarDateSelect} />
                    </div>

                    <div className="working-hours-header">
                        <div className="working-hours-col">Start</div>
                        <div className="working-hours-col">End</div>
                        <div className="working-hours-col">Nail Tech</div>
                        {/* <div className="working-hours-actions-header"></div> */}
                    </div>

                    {daysOfWeek.map((day, index) => {
                        const startOfWeek = new Date(selectedWeek);
                        const suggestedDate = new Date(startOfWeek);
                        suggestedDate.setDate(startOfWeek.getDate() + index + 2);

                        return (

                            <div className="day-container" key={day}>
                                <div className="day-header-container">
                                    <h3 className="day-header">{day + " - " + formatMonthDate(suggestedDate)}</h3>
                                    <div className="DayHeaderActions">
                                        {editingDay === day ? (
                                            <>
                                                <button onClick={() => setEditingDay(null)}>
                                                    <img className="DoneIcon" src="/images/DoneIcon.png" alt="Done Icon" />
                                                    Done
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    if (editingDay === null) {
                                                        setEditingDay(day);
                                                    } else {
                                                        alert("Please save before editing another day.");
                                                    }
                                                }}
                                            >
                                                <img className="EditIcon" src="/images/EditIcon.png" alt="Edit Icon" />
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <ul className="working-hours-list">
                                    {groupedByDay[day].map((hour) => (
                                        <li key={hour._id} className="working-hours-row">
                                            <div className="working-hours-col" style={{ position: "relative" }}>
                                                {editingDay === day && (
                                                    <button
                                                        onClick={() => {
                                                            if (window.confirm("Are you sure you want to delete this working hour?")) {
                                                                handleDeleteWorkingHour(hour._id);
                                                            }
                                                        }}
                                                        className="delete-button"
                                                    >
                                                        –
                                                    </button>
                                                )}
                                                {new Date(hour.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                            </div>
                                            <div className="working-hours-col">
                                                {new Date(hour.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                            </div>
                                            <div className="working-hours-col">{hour.nailtech}</div>

                                        </li>
                                    ))}
                                    {editingDay === day && (
                                        <li className="working-hours-row">
                                            <div className="working-hours-col" style={{ position: "relative" }}>
                                                {/* <button
                                                    onClick={() => console.log("cancel addition")}
                                                    className="cancel-button"
                                                >
                                                    x
                                                </button> */}
                                                <input
                                                    type="time"
                                                    className="start-input"
                                                    value={newHour.start}
                                                    onChange={(e) => setNewHour({ ...newHour, start: e.target.value })}
                                                />
                                            </div>
                                            <div className="working-hours-col">
                                                <input
                                                    type="time"
                                                    className="end-input"
                                                    value={newHour.end}
                                                    onChange={(e) => setNewHour({ ...newHour, end: e.target.value })}
                                                />
                                            </div>
                                            <div className="working-hours-col">
                                                <select
                                                    className="nailtech-input"
                                                    value={newHour.nailtech}
                                                    onChange={(e) => setNewHour({ ...newHour, nailtech: e.target.value })}
                                                >
                                                    <option>Leah</option>
                                                    <option>Kayla</option>
                                                    <option>Kelly</option>
                                                </select>
                                            </div>
                                            <button
                                                onClick={() => handleAddWorkingHour(day, index)}
                                                className="add-button"
                                            >
                                                <img className="InsertIcon" src="/images/EnterArrow.png" alt="Add Hours Icon" />
                                            </button>
                                        </li>
                                    )}
                                </ul>

                            </div>
                        )
                    })}
                </div>
            </div >
        </>
    );
};

export default WorkingHours;
